import React, { FC, Fragment, useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import OrdersReadyForTransport from 'components/transport/delivery-planner/orders-ready-for-transport.component';
import NewDeliveryPanel from 'components/transport/delivery-planner/new-delivery-panel.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ClinicDeliveryOrders } from 'models/transport';
import { useLazyQuery } from '@apollo/client';
import { clinicDeliveryOrdersTemplate } from 'apollo/templates/transport/query.graphql';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import { Order } from 'models/order';
import { Organization } from 'models/organization';

type DeliveryPlannerProps = {
    onShipmentCreate: () => void;
};

const DeliveryPlanner: FC<DeliveryPlannerProps> = ({ onShipmentCreate }: DeliveryPlannerProps) => {
    const { t: translate } = useTranslation(['transport', 'orders']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [clinicOrders, setClinicOrders] = useState<ClinicDeliveryOrders[]>([]);
    const [currentDeliveryClinic, setCurrentDeliveryClinic] = useState<Organization>();
    const [addedOrders, setAddedOrders] = useState<Order[]>([]);
    const [noOfManualPackages, setNoOfManualPackages] = useState<number>();

    const [clinicDeliveryOrders, { loading, refetch }] = useLazyQuery(clinicDeliveryOrdersTemplate, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onError: (error) => {
            showBoundary(error);
        },
        onCompleted: (data) => {
            setClinicOrders(data.clinicDeliveryOrders);
        },
    });

    const refetchAfterRemoveDelivery = () => {
        refetch();
    };

    useEffect(() => {
        clinicDeliveryOrders();
    }, []);

    const addOrder = (clinic: Organization, order: Order) => {
        if (!currentDeliveryClinic || currentDeliveryClinic._id !== clinic._id) {
            setCurrentDeliveryClinic(clinic);
            setAddedOrders([]);
        }
        setAddedOrders([...addedOrders, order]);
    };

    const removeOrder = (orderId: string) => {
        setAddedOrders(addedOrders.filter((e) => e._id !== orderId));
        if (addedOrders.length - 1 === 0 && !noOfManualPackages) {
            setCurrentDeliveryClinic(undefined);
        }
    };

    const handleManualPackagesChanged = (noOfPackages: number | undefined) => {
        setNoOfManualPackages(noOfPackages);
        if (addedOrders.length - 1 === 0 && !noOfPackages) {
            setCurrentDeliveryClinic(undefined);
        }
    };

    const handleShipmentCreate = (clinicId: string, orderIds: string[]) => {
        const cOrders = JSON.parse(JSON.stringify(clinicOrders || []));
        const cOrder = cOrders.find((cOrder: ClinicDeliveryOrders) => cOrder.clinic._id === clinicId);
        if (!cOrder?.orders) {
            return;
        }
        const orders = [...(cOrder?.orders || [])];
        for (const orderId of orderIds) {
            const index = orders.findIndex((deliveryOrder) => {
                return deliveryOrder.orderId === orderId;
            });
            if (index > -1) {
                orders.splice(index, 1);
            }
        }
        cOrder.orders = orders;
        setClinicOrders(cOrders);
        setAddedOrders([]);
        onShipmentCreate();
    };

    return (
        <Fragment>
            <Grid container spacing={4}>
                <Grid item xs={9}>
                    <Box display="flex" mb={1}>
                        <Box flexGrow={1} alignItems="center">
                            <Typography gutterBottom variant="h2" sx={{ marginBottom: 1 }}>
                                {translate('orders:ORDERS')}
                            </Typography>
                        </Box>
                    </Box>
                    {loading ? (
                        <DelayedLinearProgress />
                    ) : (
                        <OrdersReadyForTransport
                            clinicOrders={clinicOrders}
                            addedOrders={addedOrders}
                            currentClinic={currentDeliveryClinic}
                            addOrder={addOrder}
                            refetchAfterRemoveDelivery={refetchAfterRemoveDelivery}
                        />
                    )}
                </Grid>
                <Grid item xs={3}>
                    <Box sx={{ position: 'sticky', top: 80, bottom: 10 }}>
                        <Typography gutterBottom variant="h2" sx={{ marginBottom: 1 }}>
                            {translate('transport:DELIVERY')}
                        </Typography>
                        <NewDeliveryPanel
                            currentClinic={currentDeliveryClinic}
                            orders={addedOrders}
                            removeOrder={removeOrder}
                            onShipmentCreate={handleShipmentCreate}
                            onClinicChanged={(clinic: Organization | undefined) => setCurrentDeliveryClinic(clinic)}
                            onManualPackagesChanged={handleManualPackagesChanged}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default DeliveryPlanner;
